<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Modal from '@/components/ui/Modal.vue'

const props = defineProps<{
  open: boolean
  config: string
  title?: string
}>()

const emit = defineEmits<{ close: [] }>()

const copied = ref(false)
const failed = ref(false)
const popping = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined

function clearTimer() {
  if (timer) {
    clearTimeout(timer)
    timer = undefined
  }
}

function resetFeedback() {
  copied.value = false
  failed.value = false
  popping.value = false
  clearTimer()
}

async function showFeedback(ok: boolean) {
  copied.value = ok
  failed.value = !ok
  popping.value = false
  await nextTick()
  popping.value = true
  clearTimer()
  timer = setTimeout(resetFeedback, 2000)
}

async function copy(config: string) {
  if (!config) return
  try {
    await navigator.clipboard.writeText(config)
    showFeedback(true)
    return
  } catch {
    // Clipboard API недоступен — запасной путь ниже.
  }

  try {
    const ta = document.createElement('textarea')
    ta.value = config
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    showFeedback(ok)
  } catch {
    showFeedback(false)
  }
}

watch(
  () => props.open,
  (open) => {
    if (!open) resetFeedback()
  },
)

watch(
  () => props.config,
  () => resetFeedback(),
)

onBeforeUnmount(clearTimer)
</script>

<template>
  <Modal
    :open="open"
    :title="title || 'Ключ'"
    max-width="lg"
    @close="emit('close')"
  >
    <div
      class="key-config mb-4 max-h-64 overflow-y-auto whitespace-pre-wrap"
      :class="copied && popping ? 'key-copied' : ''"
    >{{ config }}</div>
    <div class="flex items-center justify-end gap-3">
      <span class="sr-only" aria-live="polite">
        {{ copied ? 'Ключ скопирован' : failed ? 'Не удалось скопировать ключ' : '' }}
      </span>
      <button
        type="button"
        class="btn btn-sm min-w-[8.5rem]"
        :class="[
          copied ? 'btn-success' : failed ? 'border-danger bg-danger text-white shadow-danger/60' : 'btn-primary',
          popping && copied ? 'copy-pop' : '',
        ]"
        @click="copy(config)"
      >
        <svg
          v-if="copied"
          class="mr-1.5 h-3.5 w-3.5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.2 7.2a1 1 0 0 1-1.4 0L3.3 9.1a1 1 0 1 1 1.4-1.4l4.1 4.1 6.5-6.5a1 1 0 0 1 1.4 0Z"
            clip-rule="evenodd"
          />
        </svg>
        {{ copied ? 'Скопировано' : failed ? 'Ошибка' : 'Копировать' }}
      </button>
    </div>
  </Modal>
</template>
